import { getArrowBindings } from 'tldraw';
import { alignedCubicBetween, connectorEnds, cubicPoint, straightCubic } from './alignedConnector';

function asRect(bounds) {
  if (!bounds) return null;
  const x = Number(bounds.x ?? bounds.minX);
  const y = Number(bounds.y ?? bounds.minY);
  const w = Number(
    bounds.w ?? bounds.width ?? (Number.isFinite(bounds.maxX) ? bounds.maxX - x : NaN)
  );
  const h = Number(
    bounds.h ?? bounds.height ?? (Number.isFinite(bounds.maxY) ? bounds.maxY - y : NaN)
  );
  if (![x, y, w, h].every(Number.isFinite)) return null;
  return { x, y, w: Math.max(0, w), h: Math.max(0, h) };
}

function pointRect(point) {
  return { x: point.x, y: point.y, w: 0, h: 0 };
}

function hasArea(rect) {
  return rect.w > 1 || rect.h > 1;
}

function bindingEnds(editor, arrowId) {
  const list = editor.getBindingsFromShape?.(arrowId, 'arrow') || [];
  let startId = null;
  let endId = null;
  for (const binding of list) {
    if (binding?.props?.terminal === 'start') startId = binding.toId;
    else if (binding?.props?.terminal === 'end') endId = binding.toId;
  }
  return { id: arrowId, startId, endId };
}

/** Arrows bound to either end, including this one. Used to line up through-points. */
function collectConnectorArrows(editor, arrowId, startId, endId) {
  const byId = new Map();
  if (arrowId && startId && endId) byId.set(arrowId, { id: arrowId, startId, endId });
  for (const shapeId of [startId, endId]) {
    if (!shapeId) continue;
    const bindings = editor.getBindingsToShape?.(shapeId, 'arrow') || [];
    for (const binding of bindings) {
      const otherId = binding?.fromId;
      if (!otherId || byId.has(otherId)) continue;
      const ends = bindingEnds(editor, otherId);
      if (ends.startId && ends.endId) byId.set(otherId, ends);
    }
  }
  return [...byId.values()];
}

function toPage(editor, shape, local) {
  const transform = editor.getShapePageTransform?.(shape.id) || editor.getShapePageTransform?.(shape);
  if (transform?.applyToPoint) return transform.applyToPoint(local);
  return {
    x: (shape.x || 0) + (local?.x || 0),
    y: (shape.y || 0) + (local?.y || 0),
  };
}

function toLocal(editor, shape, page) {
  const point = editor.getPointInShapeSpace?.(shape, page);
  if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
    return { x: point.x, y: point.y };
  }
  return { x: page.x - (shape.x || 0), y: page.y - (shape.y || 0) };
}

function mapCurve(curve, mapPoint) {
  const start = mapPoint(curve.start);
  const cp1 = mapPoint(curve.cp1);
  const cp2 = mapPoint(curve.cp2);
  const end = mapPoint(curve.end);
  return {
    start,
    cp1,
    cp2,
    end,
    mid: cubicPoint(start, cp1, cp2, end, 0.5),
    startSide: curve.startSide,
    endSide: curve.endSide,
  };
}

/**
 * Edge-aligned cubic in arrow-local space.
 * Bound ends attach to the facing side of the card. Free ends stay put.
 */
export function getAlignedArrowLocalCurve(editor, shape) {
  const startLocal = shape?.props?.start || { x: 0, y: 0 };
  const endLocal = shape?.props?.end || { x: 0, y: 0 };
  const fallback = straightCubic(startLocal, endLocal);
  if (!editor || !shape) return fallback;

  try {
    const bindings = getArrowBindings(editor, shape) || {};
    const startPage = toPage(editor, shape, startLocal);
    const endPage = toPage(editor, shape, endLocal);
    const startRect = asRect(
      bindings.start && editor.getShapePageBounds?.(bindings.start.toId)
    ) || pointRect(startPage);
    const endRect = asRect(
      bindings.end && editor.getShapePageBounds?.(bindings.end.toId)
    ) || pointRect(endPage);

    const startId = bindings.start?.toId;
    const endId = bindings.end?.toId;
    let pageCurve;
    if (hasArea(startRect) && hasArea(endRect) && startId && endId) {
      const ends = connectorEnds(
        shape.id,
        collectConnectorArrows(editor, shape.id, startId, endId),
        id => asRect(editor.getShapePageBounds?.(id))
      );
      pageCurve = alignedCubicBetween(startRect, endRect, ends || undefined);
    } else if (hasArea(startRect) || hasArea(endRect)) {
      pageCurve = alignedCubicBetween(startRect, endRect);
    } else {
      pageCurve = straightCubic(startPage, endPage);
    }

    return mapCurve(pageCurve, point => toLocal(editor, shape, point));
  } catch {
    return fallback;
  }
}

/** Same curve in page space, for labels and panels. */
export function getAlignedArrowPageCurve(editor, shape) {
  const local = getAlignedArrowLocalCurve(editor, shape);
  if (!local || !editor) return null;
  try {
    return mapCurve(local, point => toPage(editor, shape, point));
  } catch {
    return null;
  }
}
